/**
 * Tabbed new/resolved findings list for scan comparison.
 */

import { useState, useCallback, useRef, KeyboardEvent } from 'react';
import { ShieldAlert, ShieldCheck, FileCode } from 'lucide-react';

interface Finding {
  id: string;
  severity: string;
  title: string;
  category: string;
  location?: { file?: string; line?: number };
}

interface SeverityCounts {
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
}

interface FindingsDiffProps {
  newFindings: Finding[];
  resolvedFindings: Finding[];
  unchangedCount: number;
  newBySeverity: SeverityCounts;
  resolvedBySeverity: SeverityCounts;
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-600 text-white',
  high: 'bg-orange-600 text-white',
  medium: 'bg-yellow-600 text-black',
  low: 'bg-blue-600 text-white',
  info: 'bg-zinc-600 text-white',
};

function SeverityBadges({ counts }: { counts: SeverityCounts }) {
  return (
    <div className="flex gap-2 flex-wrap">
      {Object.entries(counts)
        .filter(([, count]) => count > 0)
        .map(([sev, count]) => (
          <span key={sev} className={`px-2 py-0.5 rounded text-xs font-medium ${SEVERITY_COLORS[sev] || 'bg-zinc-600'}`}>
            {count} {sev}
          </span>
        ))}
    </div>
  );
}

function FindingRow({ finding }: { finding: Finding }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-zinc-800 last:border-0">
      <span className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 mt-0.5 ${SEVERITY_COLORS[finding.severity] || 'bg-zinc-600'}`}>
        {finding.severity}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-white">{finding.title}</p>
        {finding.location?.file && (
          <div className="flex items-center gap-1 mt-0.5 text-xs text-zinc-500">
            <FileCode className="w-3 h-3" />
            <span className="font-mono truncate">
              {finding.location.file}
              {finding.location.line ? `:${finding.location.line}` : ''}
            </span>
          </div>
        )}
      </div>
      <span className="text-xs text-zinc-500 shrink-0">{finding.category}</span>
    </div>
  );
}

const TABS = ['new', 'resolved'] as const;
type Tab = (typeof TABS)[number];

const FindingsDiff = ({ newFindings, resolvedFindings, unchangedCount, newBySeverity, resolvedBySeverity }: FindingsDiffProps) => {
  const [tab, setTab] = useState<Tab>('new');
  const tabRefs = useRef<Record<Tab, HTMLButtonElement | null>>({ new: null, resolved: null });

  const handleTabKeyDown = useCallback((e: KeyboardEvent<HTMLButtonElement>) => {
    const currentIndex = TABS.indexOf(tab);
    let nextIndex = -1;

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % TABS.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + TABS.length) % TABS.length;
    } else if (e.key === 'Home') {
      nextIndex = 0;
    } else if (e.key === 'End') {
      nextIndex = TABS.length - 1;
    }

    if (nextIndex >= 0) {
      e.preventDefault();
      const nextTab = TABS[nextIndex];
      setTab(nextTab);
      tabRefs.current[nextTab]?.focus();
    }
  }, [tab]);

  return (
    <div>
      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-red-950/30 border border-red-900/50 rounded-lg p-3 text-center">
          <ShieldAlert className="w-5 h-5 text-red-400 mx-auto mb-1" aria-hidden="true" />
          <p className="text-2xl font-bold text-red-400">{newFindings.length}</p>
          <p className="text-xs text-red-400/70">New findings</p>
        </div>
        <div className="bg-green-950/30 border border-green-900/50 rounded-lg p-3 text-center">
          <ShieldCheck className="w-5 h-5 text-green-400 mx-auto mb-1" aria-hidden="true" />
          <p className="text-2xl font-bold text-green-400">{resolvedFindings.length}</p>
          <p className="text-xs text-green-400/70">Resolved</p>
        </div>
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-zinc-400 mt-6">{unchangedCount}</p>
          <p className="text-xs text-zinc-500">Unchanged</p>
        </div>
      </div>

      {/* Tabs — WAI-ARIA tablist pattern with keyboard navigation */}
      <div role="tablist" aria-label="Findings comparison" className="flex gap-1 mb-3 border-b border-zinc-700">
        <button
          ref={(el) => { tabRefs.current.new = el; }}
          role="tab"
          id="findings-tab-new"
          aria-selected={tab === 'new'}
          aria-controls="findings-panel-new"
          tabIndex={tab === 'new' ? 0 : -1}
          onClick={() => setTab('new')}
          onKeyDown={handleTabKeyDown}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'new'
              ? 'border-red-500 text-red-400'
              : 'border-transparent text-zinc-400 hover:text-zinc-200'
          }`}
        >
          New ({newFindings.length})
        </button>
        <button
          ref={(el) => { tabRefs.current.resolved = el; }}
          role="tab"
          id="findings-tab-resolved"
          aria-selected={tab === 'resolved'}
          aria-controls="findings-panel-resolved"
          tabIndex={tab === 'resolved' ? 0 : -1}
          onClick={() => setTab('resolved')}
          onKeyDown={handleTabKeyDown}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'resolved'
              ? 'border-green-500 text-green-400'
              : 'border-transparent text-zinc-400 hover:text-zinc-200'
          }`}
        >
          Resolved ({resolvedFindings.length})
        </button>
      </div>

      {/* Severity breakdown */}
      <div className="mb-3">
        <SeverityBadges counts={tab === 'new' ? newBySeverity : resolvedBySeverity} />
      </div>

      {/* Finding list — tabpanel with proper association */}
      <div
        role="tabpanel"
        id={`findings-panel-${tab}`}
        aria-labelledby={`findings-tab-${tab}`}
        tabIndex={0}
        className="bg-zinc-900/50 border border-zinc-800 rounded-lg px-4 py-1 max-h-96 overflow-y-auto"
      >
        {(tab === 'new' ? newFindings : resolvedFindings).length === 0 ? (
          <p className="text-sm text-zinc-500 py-4 text-center">
            {tab === 'new' ? 'No new findings introduced.' : 'No findings resolved.'}
          </p>
        ) : (
          (tab === 'new' ? newFindings : resolvedFindings).map((f) => (
            <FindingRow key={f.id} finding={f} />
          ))
        )}
      </div>
    </div>
  );
};

export default FindingsDiff;
